/**
 * ls-theme.ts — Life Suite theme bridge.
 *
 * Adopted from lifesuite-infra v1.0.0 template/client/src/lib/ls-theme.ts.
 * Bridges LS design tokens into the running LS child app. Once
 * @goodbetter/design-tokens ships, this file will re-export from there.
 */

export type LsThemeMode = "light" | "dark";

/**
 * Apply LS theme tokens to the document root. Should be called once at app boot,
 * BEFORE first paint, to avoid theme flash. In v1.0.0 this just toggles the dark
 * class; once design-tokens ships it will inject CSS custom properties.
 */
export function applyLsTheme(mode: LsThemeMode): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (mode === "dark") {
    root.classList.add("dark");
    root.setAttribute("data-ls-theme", "dark");
  } else {
    root.classList.remove("dark");
    root.setAttribute("data-ls-theme", "light");
  }
}

/**
 * Read the user's preferred theme, in priority order:
 *   1. localStorage 'ls-theme'
 *   2. system preference (prefers-color-scheme)
 *   3. fallback: 'dark' (LS default)
 */
export function resolveLsTheme(): LsThemeMode {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem("ls-theme");
  if (stored === "light" || stored === "dark") return stored;
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  return "dark";
}

/** Persist the user's choice and apply it. */
export function setLsTheme(mode: LsThemeMode): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem("ls-theme", mode);
  }
  applyLsTheme(mode);
}
